import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators, ValidatorFn } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NgSelectComponent, NgLabelTemplateDirective, NgOptionTemplateDirective } from '@ng-select/ng-select';
import { CountryCodes } from '../../../models/country-codes';
import { Subscription } from 'rxjs';
import { customValidator } from '../../../../Utils/validator';
@Component({
  selector: 'app-mobile-number',
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    NgSelectComponent,
    NgLabelTemplateDirective,
    NgOptionTemplateDirective
  ],
  templateUrl: './mobile-number.component.html',
  styleUrl: './mobile-number.component.scss'
})
export class MobileNumberComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('inputElement') inputElementRef!: ElementRef;
  @Input() control!: any;
  @Input() countryCodeControl!: any;
  @Input() label!: string;
  @Input() type: string = 'text';
  @Input() placeholder: string = '';
  @Input() errorMessage: string = 'This field is required';
  @Input() options: any = '';
  @Input() validationMessages: { [key: string]: string } = {};
  @Input() country = false;
  @Input() numberType: 'mobile' | 'landline' = 'mobile';

  countryCodes = CountryCodes.LIST;

  private baseValidators: ValidatorFn[] = [];
  private countryCodeSub?: Subscription;
  private validatorsCaptured = false;

  ngOnInit(): void {
    this.captureBaseValidators();
    this.applyPhoneValidators(this.countryCodeControl?.value);
    this.watchCountryCodeChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['control'] || changes['countryCodeControl']) {
      this.captureBaseValidators(true);
      this.applyPhoneValidators(this.countryCodeControl?.value);
      this.watchCountryCodeChanges();
    }
  }

  ngOnDestroy(): void {
    this.countryCodeSub?.unsubscribe();
  }

  get isRequired(): boolean {
    const hasRequired = this.control?.hasValidator?.(Validators.required);
    const hasRequiredTrue = this.control?.hasValidator?.(Validators.requiredTrue);
    return Boolean(hasRequired || hasRequiredTrue);
  }
  // This method is used to error keys
  get errorKeys(): string[] {
    return this.control?.errors ? Object.keys(this.control.errors) : [];
  }

  // Show only the most relevant error (e.g., UAE-specific over generic)
  get primaryErrorKey(): string | null {
    const keys = this.errorKeys;
    if (!keys.length) {
      return null;
    }
    const priority = ['uaeMobile', 'uaePhoneNumber', 'uaeLandline', 'mobile'];
    const match = priority.find(key => keys.includes(key));
    return match ?? keys[0];
  }
  // This method is used to check if mobile valid
  isMobileValid(): boolean {
    return /^[0-9]{7,18}$/.test(this.control.value);
  }
  // This method is used to input classes
  get inputClasses(): string {
    // base
    let cls = 'form-input w-full px-3 py-2 text-dc-navy font-light transition ' +
      'border focus:outline-none disabled:cursor-not-allowed disabled:bg-[#eeeeee]';

    // normal / states
    if (this.control.touched && this.control.invalid) {
      return cls + ' border-red-600';
    }
    if (this.control.touched && this.control.valid) {
      return cls + ' border-green-600';
    }
    return cls + ' border-dc-navy';
  }
  // This method is used to focus
  focus(): void {
    const el = this.inputElementRef?.nativeElement;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus();
    }
  }

  private watchCountryCodeChanges(): void {
    this.countryCodeSub?.unsubscribe();
    if (!this.countryCodeControl?.valueChanges) {
      return;
    }
    this.countryCodeSub = this.countryCodeControl.valueChanges.subscribe((code: any) => {
      this.applyPhoneValidators(code);
    });
  }

  private captureBaseValidators(force: boolean = false): void {
    if (!this.control || (this.validatorsCaptured && !force)) {
      return;
    }

    const rawValidators: ValidatorFn[] = (this.control as any)?._rawValidators || [];
    const filteredValidators: ValidatorFn[] = [];
    let hasLandlineValidator = false;

    rawValidators.forEach((validator: ValidatorFn) => {
      const { isPhone, isLandline } = this.checkPhoneValidator(validator);
      if (isLandline) {
        hasLandlineValidator = true;
      }
      if (!isPhone) {
        filteredValidators.push(validator);
      }
    });

    // fallback if no rawValidators are available
    if (!filteredValidators.length && this.control.validator) {
      const fallbackValidator = this.control.validator;
      const { isPhone, isLandline } = this.checkPhoneValidator(fallbackValidator);
      if (isLandline) {
        hasLandlineValidator = true;
      }
      if (!isPhone && fallbackValidator) {
        filteredValidators.push(fallbackValidator);
      }
    }

    if (hasLandlineValidator) {
      this.numberType = 'landline';
    }

    this.baseValidators = filteredValidators;
    this.validatorsCaptured = true;
  }

  private applyPhoneValidators(countryCodeValue: any): void {
    if (!this.control) {
      return;
    }

    const normalizedCode = (countryCodeValue || '').toString().replace(/\+/g, '').trim();
    const isUae = normalizedCode === '971';
    const dynamicValidator = this.numberType === 'landline'
      ? (isUae ? customValidator('uaePhoneNumber') : customValidator('mobile'))
      : (isUae ? customValidator('uaeMobile') : customValidator('mobile'));

    this.control.setValidators([...this.baseValidators, dynamicValidator]);
    this.control.updateValueAndValidity({ emitEvent: false });
  }

  private checkPhoneValidator(validator: ValidatorFn | null | undefined): { isPhone: boolean; isLandline: boolean } {
    const result = { isPhone: false, isLandline: false };
    if (!validator) {
      return result;
    }

    const validatorType = (validator as any)?.__validatorType;
    if (validatorType) {
      result.isPhone = validatorType === 'mobile' ||
        validatorType === 'uaeMobile' ||
        validatorType === 'uaeLandline' ||
        validatorType === 'uaePhoneNumber' ||
        validatorType === 'landline';
      result.isLandline = validatorType === 'uaeLandline' || validatorType === 'uaePhoneNumber' || validatorType === 'landline';
      return result;
    }

    const validatorSource = validator.toString();
    result.isLandline = validatorSource.includes('uaePhoneNumber') || validatorSource.includes('uaeLandline');
    result.isPhone = result.isLandline || validatorSource.includes('uaeMobile') || validatorSource.includes('mobile');
    return result;
  }
}
